import React, { useState } from "react";
import { Button } from "../common/Button";
import { useStepData } from "../../hooks/useStepData";
import { useFunnelNavigation } from "../../hooks/useFunnelNavigation";
import { InfoBox } from "../common/InfoBox";
import { InputField } from "../common/InputField";
import { StepHeader } from "../common/StepHeader";
import { useMeasurementSystem } from "../../hooks/useMeasurementSystem";
import { MeasurementToggle } from "../common/MeasurementToggle";

const HEIGHT_LIMITS = {
  imperial: {
    feet: { min: 4, max: 8 },
    inches: { min: 0, max: 11 },
  },
  metric: {
    cm: { min: 120, max: 250 },
  },
};

const HeightInput = ({ config }) => {
  const { measurementSystem } = useMeasurementSystem();
  const { data, updateStepData } = useStepData(config.field);
  const { nextStep } = useFunnelNavigation();

  const [measurements, setMeasurements] = useState({
    feet: data?.feet || "",
    inches: data?.inches || "",
    cm: data?.cm || "",
  });
  const [unit, setUnit] = useState(measurementSystem);

  const [errors, setErrors] = useState({
    feet: "",
    inches: "",
    cm: "",
  });

  // Modify handleInputChange to include validation
  const handleInputChange = (field, value) => {
    const numValue = Number(value);
    let errorMessage = "";

    if (value !== "") {
      const limits =
        field === "cm"
          ? HEIGHT_LIMITS.metric.cm
          : HEIGHT_LIMITS.imperial[field];

      if (numValue < limits.min) {
        errorMessage = `Minimum ${field} is ${limits.min}`;
      } else if (numValue > limits.max) {
        errorMessage = `Maximum ${field} is ${limits.max}`;
      }
    }

    setErrors((prev) => ({
      ...prev,
      [field]: errorMessage,
    }));

    setMeasurements((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isValidImperial = () => {
    const { feet, inches } = measurements;
    const { feet: feetLimits, inches: inchesLimits } = HEIGHT_LIMITS.imperial;
    return (
      feet >= feetLimits.min &&
      feet <= feetLimits.max &&
      inches >= inchesLimits.min &&
      inches <= inchesLimits.max
    );
  };

  const isValidMetric = () => {
    const { cm } = measurements;
    const { cm: cmLimits } = HEIGHT_LIMITS.metric;
    return cm >= cmLimits.min && cm <= cmLimits.max;
  };

  const hasInput = () => {
    return unit === "imperial"
      ? measurements.feet !== "" || measurements.inches !== ""
      : measurements.cm !== "";
  };

  const handleNext = () => {
    const isValid = unit === "imperial" ? isValidImperial() : isValidMetric();
    if (isValid) {
      const dataToUpdate =
        unit === "imperial"
          ? { feet: measurements.feet, inches: measurements.inches, unit }
          : { cm: measurements.cm, unit };
      updateStepData(dataToUpdate);
      nextStep();
    }
  };

  return (
    <div className="flex flex-col px-4">
      <div className="space-y-4">
        <StepHeader
          title={config.title}
          subtitle={config.subtitle}
          order={config.headerOrder}
        />

        <MeasurementToggle onChange={setUnit} />

        <div className="flex justify-center gap-4">
          {unit === "imperial" ? (
            <>
              <InputField
                value={measurements.feet}
                onChange={(value) => handleInputChange("feet", value)}
                placeholder="5"
                unit="ft"
                type="number"
                error={errors.feet}
                min={HEIGHT_LIMITS.imperial.feet.min}
                max={HEIGHT_LIMITS.imperial.feet.max}
              />
              <InputField
                value={measurements.inches}
                onChange={(value) => handleInputChange("inches", value)}
                placeholder="8"
                unit="in"
                type="number"
                error={errors.inches}
                min={HEIGHT_LIMITS.imperial.inches.min}
                max={HEIGHT_LIMITS.imperial.inches.max}
              />
            </>
          ) : (
            <InputField
              value={measurements.cm}
              onChange={(value) => handleInputChange("cm", value)}
              placeholder="170"
              unit="cm"
              type="number"
              error={errors.cm}
              min={HEIGHT_LIMITS.metric.cm.min}
              max={HEIGHT_LIMITS.metric.cm.max}
            />
          )}
        </div>

        <InfoBox
          icon="info"
          title="Calculating your BMI"
          text={config.description}
        />

        {hasInput() && !Object.values(errors).some((error) => error !== "") && (
          <div className="mt-6">
            <Button
              onClick={handleNext}
              className="w-full"
              disabled={
                (unit === "imperial" && !isValidImperial()) ||
                (unit === "metric" && !isValidMetric())
              }
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export { HeightInput };
